import { useState } from 'react'

import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { SubmitHandler, useForm } from 'react-hook-form'

import * as EmailValidator from 'email-validator'

import {
  ManagedAccountsDocument,
  PendingInvitesDocument,
  useInviteUserMutation,
  useUpdateUserRolesMutation,
  useUserLazyQuery,
  useUsersLazyQuery,
} from '@/graphql/generated/hooks'
import { BUTTON_PRESS, mixpanel, trackEvent } from '@/utils/analytics'
import { getErrorMessage } from '@/utils/errorMessage'

import { RoleExtended } from '../types/types'
import {
  VALID_ROLE_VALUES,
  extractExistingRoles,
  findUserIdByEmail,
  mergeRoles,
} from '../utils/helpers'

interface FormInputsIProps {
  firstName: string
  lastName: string
  email: string
  isSsoUser: boolean
}

interface UseInviteUserModalIProps {
  onClose: () => void
  preSelectedRole?: RoleExtended
}

const computeInitialRoles = (preSelectedRole?: RoleExtended): string[] =>
  preSelectedRole && VALID_ROLE_VALUES.includes(preSelectedRole)
    ? [preSelectedRole]
    : []

export const useInviteUserModal = ({
  onClose,
  preSelectedRole,
}: UseInviteUserModalIProps) => {
  const { shouldEnableCreateUser } = usePermissions()
  const { showSuccess, showError } = useToasts()

  // State
  const [selectedRoles, setSelectedRoles] = useState<string[]>(() =>
    computeInitialRoles(preSelectedRole)
  )

  // Form
  const { handleSubmit, register, errors } = useForm<FormInputsIProps>({
    mode: 'onChange',
    defaultValues: {
      isSsoUser: false,
    },
  })

  // Queries
  const [fetchUsersByEmail] = useUsersLazyQuery({ fetchPolicy: 'network-only' })
  const [fetchUserById] = useUserLazyQuery({ fetchPolicy: 'network-only' })

  // Mutations
  const [inviteUser, { loading: isLoadingInviteUser }] = useInviteUserMutation()
  const [updateUserRoles, { loading: isLoadingUpdateRoles }] =
    useUpdateUserRolesMutation()

  const onSubmit: SubmitHandler<FormInputsIProps> = async (values) => {
    const validSelectedRoles = selectedRoles.filter((r) =>
      VALID_ROLE_VALUES.includes(r)
    )

    trackEvent(`${BUTTON_PRESS} Invite User Submit`, {
      roles_count: validSelectedRoles.length,
      is_sso_user: values.isSsoUser,
    })

    // Detect whether a user with this email already exists. If so, add the
    // selected roles to the existing user via updateUserRoles rather than
    // attempting to (re-)invite and hitting a duplicate-email failure.
    let existingUserId: string | null = null
    let didLookupFail = false
    try {
      const { data: usersData, error: lookupError } = await fetchUsersByEmail({
        variables: { filter: { email: values.email } },
      })
      if (lookupError) throw lookupError
      existingUserId = findUserIdByEmail(usersData, values.email)
    } catch (error) {
      didLookupFail = true
      Sentry.captureException(error, {
        tags: { feature: 'managed_accounts', action: 'lookup_user_by_email' },
      })
    }

    if (didLookupFail) {
      // Stop the submission — falling through to inviteUser would risk the
      // exact duplicate-email failure this flow is supposed to prevent.
      showError(
        "Couldn't verify email against existing users. Please try again."
      )
      return
    }

    if (existingUserId) {
      try {
        const { data: userData } = await fetchUserById({
          variables: { id: existingUserId },
        })
        const existingRoles = extractExistingRoles(userData)
        const mergedRoles = mergeRoles(existingRoles, validSelectedRoles)

        await updateUserRoles({
          variables: {
            input: {
              id: existingUserId,
              roles: mergedRoles,
            },
          },
          refetchQueries: [
            { query: ManagedAccountsDocument },
            { query: PendingInvitesDocument },
          ],
        })
        trackEvent('Invite User Success', {
          roles_count: mergedRoles.length,
          is_sso_user: values.isSsoUser,
          existing_user: true,
        })
        onClose()
        showSuccess(`Roles updated for ${values.email}`)
      } catch (error) {
        trackEvent('Invite User Failure', {
          error: getErrorMessage(error),
          existing_user: true,
        })
        showError(getErrorMessage(error))
      }
      return
    }

    const data = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      roles: validSelectedRoles,
      isSsoUser: values.isSsoUser,
    }

    try {
      await inviteUser({
        variables: {
          input: data,
        },
        refetchQueries: [
          {
            query: ManagedAccountsDocument,
          },
          {
            query: PendingInvitesDocument,
          },
        ],
      })
      mixpanel.track('Invite User Success', {
        rolesCount: validSelectedRoles.length,
        isSsoUser: values.isSsoUser,
      })
      onClose()
      showSuccess(`Invitation sent to ${values.email}`)
    } catch (error) {
      mixpanel.track('Invite User Failure', {
        error: getErrorMessage(error),
      })
      showError(getErrorMessage(error))
    }
  }

  // Validation rules
  const validationRules = {
    firstName: {
      required: 'Please add first name',
    },
    lastName: {
      required: 'Please add last name',
    },
    email: {
      required: 'You must specify an email',
      validate: {
        isEmail: (v: string) =>
          EmailValidator.validate(v) || 'Invalid email address',
      },
    },
  }

  // Employees are intentionally roleless — allow submitting with zero roles
  // selected when the "+" came from the Employee card.
  const isEmployeeFlow = preSelectedRole === RoleExtended.EMPLOYEE
  const isSubmitDisabled =
    !shouldEnableCreateUser || (!isEmployeeFlow && selectedRoles.length === 0)

  return {
    // Form
    register,
    errors,
    handleFormSubmit: handleSubmit(onSubmit),
    validationRules,

    // State
    selectedRoles,
    setSelectedRoles,

    // Submission
    isLoadingInviteUser: isLoadingInviteUser || isLoadingUpdateRoles,
    isSubmitDisabled,
  }
}
